import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Affix } from 'antd';

import { ReactComponent as OthersIcon } from 'assets/images/others.svg';
import { homePageTabItems, orderTabItems } from 'central-utils/tabUtils';
import CustomTab from 'components/CustomTab';
import DefaultHeaderContent from 'components/header/DefaultHeaderContent';
import SearchBarHeader from 'components/header/SearchBarHeader';
import { useAppSelector } from 'store/customHooks';
import { type TabItem } from 'types';

const toTitleCase = (str: string): string =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Stable numeric ID derived from category name so sessionStorage tab restoration works
// across page reloads even for dynamic categories.
const getCategoryTabId = (name: string): number => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) % 65536;
  }
  return 1000 + hash;
};

const CustomHeader: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;

  const { disableConfirmedTab, availableCategories } = useAppSelector((state) => state.header);
  const { isOrderTimeClosed } = useAppSelector((state) => state.tableStatusAndOrder);

  const { headerLogo, altName } = useAppSelector((state) => state.header);

  /**
   * Builds the ordered list of home page tabs based on categories returned by the API.
   * Steps:
   * 1. Extract the "For You" tab — it always appears first regardless of backend order.
   * 2. Build a case-insensitive lookup map (label → tab item) from all predefined tab items.
   * 3. Iterate over `availableCategories` (which defines the backend-driven display order).
   *    - If a category matches a predefined tab (Drink, Eat, Smoke) use it (preserving icon).
   *    - Otherwise create a dynamic tab with the OthersIcon and a stable derived ID.
   * 4. Return the final array: ["For You", ...ordered category tabs].
   */
  const updatedHomePageTab = useMemo(() => {
    const forYouTab = homePageTabItems.find((item) => item.label === 'For You')!;
    const predefinedByLabel = new Map(
      homePageTabItems.map((item) => [item.label.toUpperCase(), item]),
    );

    const orderedTabs: TabItem[] = availableCategories
      .filter((cat) => cat !== 'FOR YOU')
      .map((category) => {
        const predefined = predefinedByLabel.get(category);
        if (predefined) return predefined;

        return {
          id: getCategoryTabId(category),
          label: toTitleCase(category),
          activeLabelClassName: 'customHeaderTabLabel home active',
          inactiveLabelClassName: 'customHeaderTabLabel home inactive',
          icon: OthersIcon,
          iconWidth: 16,
          iconHeight: 16,
          type: 'TAB' as const,
          disabled: false,
        };
      });

    return [forYouTab, ...orderedTabs];
  }, [availableCategories]);

  const updatedOrderPageTab = useMemo(
    () =>
      orderTabItems.map((item) => {
        if (item.label === 'Completed') {
          return {
            ...item,
            disabled: disableConfirmedTab,
          };
        } else if (item.label === 'Pending') {
          return {
            ...item,
            disabled: isOrderTimeClosed,
          };
        }
        return item;
      }),
    [disableConfirmedTab, isOrderTimeClosed],
  );

  useEffect(() => {
    if (isOrderTimeClosed) {
      sessionStorage.setItem('activeOrderHeaderTab', JSON.stringify(updatedOrderPageTab[1].id));
    }
  }, [isOrderTimeClosed, updatedOrderPageTab]);

  // Paths where the header should not be displayed
  const pathsWithoutHeader: string[] = [];

  // If the current path is in the pathsWithoutHeader array, do not render the header
  if (pathsWithoutHeader.includes(pathname)) {
    return null;
  }

  const handleHeaderContent = () => {
    switch (pathname) {
      case '/':
      case '/check-out':
      case '/customer-details':
      case '/verify-otp':
      case '/enter-name':
      case '/verify-pin':
      case '/payment-confirmation':
      case '/payment-in-progress-block':
      case '/payment-status':
      case '/terms/conditions':
      case '/terms/service':
      case '/terms/faq':
      case '/terms/faqs':
      case '/bill-pending':
      case '/session-expired':
        return (
          <div className='flex justify-center relative items-center plainIconHeader'>
            {headerLogo && headerLogo?.length && (
              <img alt={altName || ''} src={headerLogo} width={200} height={60} />
            )}
          </div>
        );

      case '/home':
        return (
          <div>
            <DefaultHeaderContent />
            <Affix offsetTop={0}>
              <CustomTab
                identifierKey='homePageHeaderTabs'
                tabItems={updatedHomePageTab}
                defaultTab={sessionStorage?.getItem('activeHomeHeaderTab')}
              />
            </Affix>
          </div>
        );

      case '/order':
        return (
          <div>
            <DefaultHeaderContent />
            <Affix offsetTop={0}>
              <CustomTab
                identifierKey='orderPageHeaderTabs'
                tabItems={updatedOrderPageTab}
                defaultTab={sessionStorage?.getItem('activeOrderHeaderTab')}
              />
            </Affix>
          </div>
        );

      case '/search':
        return (
          <Affix offsetTop={0}>
            <SearchBarHeader />
          </Affix>
        );

      default:
        return (
          <Affix offsetTop={0}>
            <DefaultHeaderContent />
          </Affix>
        );
    }
  };

  return handleHeaderContent();
};

export default CustomHeader;
